import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { catchError, Observable, of } from "rxjs";

import { IQueryCommand } from "@features/filter";
import { AccountStatus } from "./account-status";
import { PagedCrudService } from "@features/grid";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { AccountService } from "@account/services";
import { ImpersonationData } from "./impersonation-data";
import { AppStorage } from "@bbwt/utils/app-storage";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service/http-responses-handler";
import { IOrganization } from "../companies/organization";
import { OrganizationService } from "../companies/organization.service";
import { LayoutEventKeys } from "@main/app-layout/layout-event-keys";
import { LayoutDataKeys } from "@main/app-layout/layout-data-keys";
import { IRole } from "@main/roles";
import { FileDetails } from "@main/file-storage";
import { CurrentUserChangedData } from "./current-user-changed-data";
import { NamingConvention } from "@mipro/classes/naming-convention";
import { OrganizationClinician } from "./organization-clinician";
import { SetLocalTimeZone, SetUTCTimeZone } from "@bbwt/utils/date-converter";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { MiproLoginBase } from "./mipro-login-base";
import { UserLogin } from "./user-login";
import { PatientLogin } from ".";
import { PersonalInfoUser } from "./personal-info-user";
import { ReviewedPatientData } from "./reviewed-patient-data";
import { NhsRegisterTokenState } from "./nhs-register-result";
import { DefaultPermissions } from "./default-permissions";
import { UserClaimsStore } from "./user-claims-store";
import { HistoricalItem } from "@mipro/classes/historical-item";

@Injectable({
    providedIn: "root"
})
export class UserService extends PagedCrudService<UserLogin> {
    static readonly CurrentUserChangedEventName = "CurrentUserChanged";
    static readonly CurrentUserEmailChangedEventName = "CurrentUserEmailChangedEventName";
    static readonly UserChangedEventName = "UserChanged";

    private readonly currentUserKey = "current-user";

    private _currentUser = AppStorage.getItem<UserLogin | PatientLogin>(this.currentUserKey);

    readonly url = "api/user";
    readonly entityTitle = "User";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private broadcastService: BroadcastService,
        private appOnlineStateService: AppOnlineStateService,
        private claimsStore: UserClaimsStore
    ) {
        super(http, handlersFactory);

        broadcastService.on<UserLogin | PatientLogin>(AccountService.UserLoginEventName).subscribe(async _ => {
            this.setCurrentUser(await this.getLoggedUser());
        });
        broadcastService.on(AccountService.UserLogoutEventName).subscribe(() => {
            if (this.isLogged) {
                AppStorage.setItem(AppStorage.LastLoggedUserId, this.currentUser.id);
                AppStorage.setItem(AppStorage.LastLoggedUserName, this.currentUser.fullName);
            }
            this.setCurrentUser(null);
        });
        broadcastService.on<IOrganization>(OrganizationService.OrganizationChangedEventName).subscribe(company => {
            if (this._currentUser.organizationId == company.id) {
                this._currentUser.organization = company;
                AppStorage.setItem(this.currentUserKey, this._currentUser);
            }
        });

        broadcastService.on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName).subscribe(x => {
            AppStorage.setItem(LayoutDataKeys.IsMenuBlockedKey, x.currentUser?.isUserRequiredSetupTwoFactor);
            broadcastService.broadcast(LayoutEventKeys.BlockMenuEventName, x.currentUser?.isUserRequiredSetupTwoFactor);
        });
    }

    get isAdmin(): boolean {
        return AppStorage.getItem<boolean>(AppStorage.AdminModeEnabledKey);
    }

    get isLogged(): boolean {
        return !!this._currentUser;
    }

    get currentUser(): UserLogin | PatientLogin {
        return { ...this._currentUser, autoSaveForms: this._currentUser?.autoSaveForms && this.appOnlineStateService.isAppOnline };
    }

    get currentUserRoles() {
        return (<UserLogin>this._currentUser)?.roles ?? [];
    }

    get isUserRequiredSetupTwoFactor(): boolean {
        return this._currentUser?.isUserRequiredSetupTwoFactor;
    }

    get impersonationData(): ImpersonationData {
        const storedData = AppStorage.getItem<ImpersonationData>(AppStorage.ImpersonationDataKey);
        return storedData || { isImpersonating: false };
    }

    async initialize(): Promise<void> {
        await this.refreshCurrentUser();

        if (this.currentUser) {
            await this.refreshImpersonationData();
        }
    }

    update(id: string, item: UserLogin, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        item.dateOfBirth = SetUTCTimeZone(item.dateOfBirth);
        return super.update(id, item, responseHandlerSettings).then(updatedUser => {
            updatedUser.dateOfBirth = SetLocalTimeZone(updatedUser.dateOfBirth);
            const oldCurrentUser = this._currentUser;
            this.broadcastService.broadcast(UserService.UserChangedEventName, updatedUser);
            if (oldCurrentUser && updatedUser.id == oldCurrentUser.id) {
                this.setCurrentUser(updatedUser);
                if (oldCurrentUser.email != updatedUser.email) {
                    this.broadcastService.broadcast(UserService.CurrentUserEmailChangedEventName, {
                        oldEmail: oldCurrentUser.email,
                        newEmail: updatedUser.email
                    });
                }
            }

            return updatedUser;
        });
    }

    updateDeliveryAddress(id: string, item: MiproLoginBase, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/${id}/set-deliveryaddress`, item),
            this.handlersFactory.getForUpdate(this.entityTitle, responseHandlerSettings)
        );
    }

    async refreshCurrentUser(responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<MiproLoginBase> {
        const currentUser = await this.getLoggedUser(responseHandlerSettings);

        this.setCurrentUser(currentUser);
        return currentUser;
    }

    getUser(id: string) {
        return this.handleRequest(this.http.get(`${this.url}/${id}`), this.handlersFactory.getDefault());
    }

    async getLoggedUser(responseHandlerSettings?: IHttpResponseHandlerSettings) {
        const user = await this.handleRequest(
            this.http.get<UserLogin | PatientLogin>(`${this.url}/me`),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );

        if (user?.dateOfBirth) {
            user.dateOfBirth = SetLocalTimeZone(user.dateOfBirth);
        }

        return user;
    }

    getLoggedUserNamingConvention(): Promise<NamingConvention> {
        return this.handleRequest(this.http.get<NamingConvention>(`${this.url}/me-name-conv`), this.handlersFactory.getDefault());
    }

    getCurrentOrganizationClinicians() {
        return this.handleRequest(
            this.http.get<OrganizationClinician[]>(`${this.url}/organization-clinicians`),
            this.handlersFactory.getDefault()
        );
    }

    sendInvite(user: MiproLoginBase): Promise<any> {
        user.dateOfBirth = SetUTCTimeZone(user.dateOfBirth);

        return this.handleRequest(this.http.post(`${this.url}/invite`, user), this.handlersFactory.getDefault({ showErrorMessage: false }));
    }

    resendInvitation(userId: string): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/${userId}/resend-invitation`, null), this.handlersFactory.getDefault());
    }

    resendEmailConfirmation(userId: string): Promise<any> {
        return this.handleRequest(
            this.http.post(`${this.url}/${userId}/resend-email-confirmation`, null),
            this.handlersFactory.getDefault()
        );
    }

    async impersonateCurrentUserAsUser(impersonatedUserId: string, organizationId: string) {
        await this.handleRequest(
            this.http.post(`${this.url}/me/${impersonatedUserId}/${organizationId}/impersonate`, null),
            this.handlersFactory.getDefault()
        );

        const impersonatedUser = await this.getLoggedUser();

        await this.refreshImpersonationData();

        this.setCurrentUser(impersonatedUser);

        return impersonatedUser;
    }

    stopImpersonationForCurrentUser(): Promise<MiproLoginBase> {
        return this.handleRequest(
            this.http.post<UserLogin | PatientLogin>(`${this.url}/me/stop-impersonation`, null),
            this.handlersFactory.getDefault()
        ).then(originalUser => {
            AppStorage.setItem(AppStorage.ImpersonationDataKey, null);
            this.setCurrentUser(originalUser);
            return originalUser;
        });
    }

    isCurrentUserImpersonating(responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<ImpersonationData> {
        return this.handleRequest<ImpersonationData>(
            this.http.get<ImpersonationData>(`${this.url}/me/is-impersonating`).pipe(catchError(_ => of({ isImpersonating: false }))),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    canCurrentUserImpersonateUser(id: string, organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/me/${id}/${organizationId}/can-impersonate`).pipe(catchError(_ => of(false))),
            this.handlersFactory.getDefault()
        );
    }

    approve(id: string): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/${id}/approve`, null), this.handlersFactory.getForUpdate(this.entityTitle));
    }

    toggleLocking(id: string): Promise<any> {
        return this.handleRequest(
            this.http.post(`${this.url}/${id}/toggle-locking`, null),
            this.handlersFactory.getForUpdate(this.entityTitle)
        );
    }

    toggleDeleting(id: string): Promise<any> {
        return this.handleRequest(
            this.http.post(`${this.url}/${id}/toggle-deleting`, null),
            this.handlersFactory.getForUpdate(this.entityTitle)
        );
    }

    getPageByApiUrl(queryCommand: IQueryCommand, apiUrl: string): Promise<any> {
        return this.handleRequest(this.http.get(apiUrl), this.handlersFactory.getDefault());
    }

    getAccountStatuses(): string[] {
        const statuses = [];
        for (const status in AccountStatus) {
            if (typeof AccountStatus[status] === "number") {
                statuses.push(AccountStatus[status]);
            }
        }
        return statuses;
    }

    getUserEmail(loginId: string): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/${loginId}/email`), this.handlersFactory.getDefault());
    }

    getUsername(loginId: string): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/${loginId}/username`), this.handlersFactory.getDefault());
    }

    updatePersonalInformation(item: PersonalInfoUser) {
        item.dateOfBirth = SetUTCTimeZone(item.dateOfBirth);
        return this.handleRequest(
            this.http.post<UserLogin | PatientLogin>(`${this.url}/update-personal-information`, item),
            this.handlersFactory.getForUpdate("Update Profile", { successMessage: "Profile successfully updated." })
        ).then(updatedUser => {
            if (this._currentUser.email != updatedUser.email) {
                this.broadcastService.broadcast(UserService.CurrentUserEmailChangedEventName, {
                    oldEmail: this._currentUser.email,
                    newEmail: updatedUser.email
                });
            }

            updatedUser.dateOfBirth = SetLocalTimeZone(updatedUser.dateOfBirth);
            this.setCurrentUser(updatedUser);
            this.broadcastService.broadcast(UserService.UserChangedEventName, updatedUser);

            return updatedUser;
        });
    }

    async updateGlobalDroTermsAccepted(state: boolean): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/me/update-globaldro-terms-accepted/${state}`, null),
            this.handlersFactory.getDefault()
        );
    }

    async reviewPatientData(reviewedData: Partial<ReviewedPatientData>) {
        try {
            reviewedData.dateOfBirth = SetUTCTimeZone(reviewedData.dateOfBirth);
            await this.handleRequest(this.http.put(`${this.url}/me/review-patient-data`, reviewedData));
            await this.refreshCurrentUser();
        } catch (error: unknown) {
            if (typeof error === "object" && "newEmail" in error) {
                this.broadcastService.broadcast(UserService.CurrentUserEmailChangedEventName, {
                    oldEmail: this._currentUser.email,
                    newEmail: reviewedData.email
                });
            }
        }
    }

    updateLoggedUser(item: UserLogin) {
        item.dateOfBirth = SetUTCTimeZone(item.dateOfBirth);
        return this.handleRequest(
            this.http.post<UserLogin>(`${this.url}/me`, item),
            this.handlersFactory.getForUpdate("Update Profile", { successMessage: "Profile successfully updated." })
        ).then(updatedUser => {
            if (this._currentUser.email != updatedUser.email) {
                this.broadcastService.broadcast(UserService.CurrentUserEmailChangedEventName, {
                    oldEmail: this._currentUser.email,
                    newEmail: updatedUser.email
                });
            }

            updatedUser.dateOfBirth = SetLocalTimeZone(updatedUser.dateOfBirth);
            this.setCurrentUser(updatedUser);
            this.broadcastService.broadcast(UserService.UserChangedEventName, updatedUser);

            return updatedUser;
        });
    }

    uploadAvatarImage(formData: FormData): Observable<HttpEvent<any>> {
        return this.http.request(new HttpRequest("POST", `${this.url}/upload-avatar-image`, formData, { reportProgress: true }));
    }

    uploadSignatureImage(formData: FormData): Observable<HttpEvent<any>> {
        return this.http.request(new HttpRequest("POST", `${this.url}/upload-signature-image`, formData, { reportProgress: true }));
    }

    getAvatarImage() {
        return this.handleRequest(this.http.get<FileDetails>(`${this.url}/get-avatar-image`), this.handlersFactory.getDefault());
    }

    getFileDetails(id_file: string): Promise<FileDetails> {
        return this.handleRequest(this.http.get<FileDetails>(`${this.url}/${id_file}/get-file-details`), this.handlersFactory.getDefault());
    }

    getUserAvatarImage(userId: string): Promise<FileDetails> {
        return this.handleRequest(this.http.get<FileDetails>(`${this.url}/${userId}/get-avatar-image`), this.handlersFactory.getDefault());
    }

    getAllRoles(): Promise<IRole[]> {
        return this.handleRequest<IRole[]>(this.http.get<IRole[]>(`${this.url}/all-roles`), this.handlersFactory.getDefault());
    }

    getDefaultPermissions() {
        return this.handleRequest(this.http.get<DefaultPermissions>(`${this.url}/default-permissions`), this.handlersFactory.getDefault());
    }

    getPermissionsDependencies(): Promise<{ [role: string]: string[][] }> {
        return this.handleRequest<{ [role: string]: string[][] }>(
            this.http.get<{ [role: string]: string[][] }>(`${this.url}/permissions-dependencies`),
            this.handlersFactory.getDefault()
        );
    }

    async updateNhsDataExportSetting(allowExport: boolean): Promise<void> {
        await this.handleRequest(
            this.http.post<void>(`${this.url}/me/update-nhs-export-setting`, { allowNhsDataExport: allowExport }),
            this.handlersFactory.getDefault()
        );
    }

    sendNhsRegistrationLink() {
        return this.handleRequest(this.http.post<void>(`${this.url}/me/nhs-register/link`, null), this.handlersFactory.getDefault());
    }

    getNhsRegistrationTokenState(patientLoginId: string, token: string) {
        const params = this.constructHttpParams({ patientLoginId, token });
        return this.handleRequest(
            this.http.get<NhsRegisterTokenState>(`${this.url}/me/nhs-register/token`, { params }),
            this.handlersFactory.getDefault({ showErrorMessage: false })
        );
    }

    linkNhs(patientLoginId: string, token: string, nhsCode: string, redirectUrl: string) {
        const nhsNonce = AppStorage.getItemFromSession<string>(AppStorage.NhsNonceKey);
        const body = { patientLoginId, token, nhsCode, redirectUrl, nhsNonce };
        return this.handleRequest(
            this.http.post<NhsRegisterTokenState>(`${this.url}/me/nhs-register`, body),
            this.handlersFactory.getDefault({ showErrorMessage: false })
        );
    }

    getHistoricalItems(userId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical/${userId}`),
            this.handlersFactory.getForReadAll());
    }

    private setCurrentUser(user: UserLogin | PatientLogin): void {
        this._currentUser = user;

        // Load avatar and branding logos. Prevent too much AWS calls
        let loadAvatar: Promise<FileDetails> = null;

        // Commented out this line, as the feature is not implemented yet
        // if (this._currentUser) {
        //     loadAvatar = this.getAvatarImage();
        // }

        Promise.all([loadAvatar]).then(([avatar]) => {
            if (this._currentUser && avatar) {
                this._currentUser.avatarImage = avatar;
            }

            const oldUser = AppStorage.getItem<MiproLoginBase>(this.currentUserKey);
            AppStorage.setItem(this.currentUserKey, this._currentUser);

            this.broadcastService.broadcast(
                UserService.CurrentUserChangedEventName,
                new CurrentUserChangedData(oldUser, this._currentUser, this.claimsStore.claims)
            );
        });
    }

    private async refreshImpersonationData(): Promise<void> {
        const impersonationData = await this.isCurrentUserImpersonating();
        AppStorage.setItem(AppStorage.ImpersonationDataKey, impersonationData);
    }
}
